/**
 * TODAY. Who is at which house on Thursday, in one look.
 *
 * `docs/architecture.md` section 6 names this as the first of the three screens that decide whether
 * the system works, and the test is one look: each visit, the home, the cleaner, the window and
 * tap-to-call, for today and tomorrow. Everything on a card is here because Victoria needs it
 * standing in a driveway, and nothing else is.
 *
 * ON A DESK IT IS A DASHBOARD. The same two days become a table, and beside them stand the four
 * questions nobody can see from a schedule: who asked and got no reply, who owes money and is late,
 * which promise the nightly run found broken, and which change is recorded and still untold. Those
 * four are the whole reason to sit at a desk rather than carry the phone, so they are the whole
 * right-hand column and the one thing under them is not a fifth question of the same kind.
 *
 * THAT LAST ONE IS A MARKETING RULE NEAR ITS THRESHOLD, and it is usually not drawn at all.
 * `docs/plan-crm-attribution.md` section 7B: "a rule within two leads or twenty dollars of firing
 * appears as one line on Today, not a notification". It is money rather than somebody waiting, so
 * it sits under the four, and on almost every morning of this business no rule is close and the
 * column looks exactly as it did before it existed. `closeToFiringPanel` in `web/src/reports.ts`
 * decides that, and a failed read is still shown: silence would say "nothing is close" and mean
 * "this did not load".
 *
 * THE PANELS ARE NOT FETCHED ON A PHONE. They are hidden below the md breakpoint, and four extra
 * requests to draw nothing is four requests on mobile data in a car. `useWideView` decides. From md
 * to xl they stand under the two days, and from xl beside them, where there is room for both.
 *
 * FOUR THINGS ARE DRAWN IN CORAL and they are the four that need doing before the day comes: a
 * visit nobody is assigned to, a change the client has not been told about yet, a home with nobody
 * on file to tell, and, since ops #85, a visit a cleaner cannot come to whose client has not chosen
 * what happens instead, with who, the word they gave and when the office heard. Since ops #74 item 5
 * a visit whose cleaners all declined it in their app is the same kind of gap, "Needs a cleaner",
 * and the schedule is the whole of how the office learns of a decline: no notice, no task, no text.
 * The second is CLAUDE.md binding rule 7 made visible: the visit is shown
 * where it still IS, because the calendar changes last, and the pill says the message has not
 * landed. All three are drawn at BOTH widths, because a gap that is only visible on a phone is a
 * gap nobody closes at a desk. The status pill itself is never coral; cancelled and no access are
 * settled facts, not gaps.
 *
 * ONE MORE THING IS CORAL, AND IT IS ABOUT THE BUSINESS RATHER THAN A VISIT: a service line with
 * visits still to come that will ask for a job sheet and find no scope in force. `openSheet` in
 * `api/src/jobsheet/sheet.ts` refuses a new job sheet then, and that refusal is read at the door,
 * by a cleaner, over the start and finish buttons. `GET /api/schedule` names each such line for
 * the business day, so the notice is drawn whatever day is on the strip, at both widths, with a
 * link to the one screen that puts it right.
 *
 * UNDER EACH DAY, EACH CLEANER'S MAKE-UP SLOT (ops #85): the one window a week the office keeps open
 * for visits that have to be moved, said open, or filled by the clean that took it. Never coral: a
 * slot kept open is the plan working, not a gap.
 *
 * The day strip is three words and a date box, and the two days shown move with it. Every day here
 * is a YYYY-MM-DD string handled by hand; the server's `today` is the one that counts.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SERVICE_LINE_LABELS, VISIT_CHANGE_KIND_LABELS, VISIT_KIND_LABELS } from '@fk/shared';
import {
  ApiError,
  getKillRulesWithinReach,
  getSchedule,
  listAttentionEnquiries,
  listBreaches,
  listInvoices,
  needsStepUp,
  type AttentionPanel,
  type BreachList,
  type CrewOnVisit,
  type Me,
  type MakeUpSlot,
  type Schedule,
  type ScheduleCallOff,
  type ScheduleRow,
  type ScopeGap,
  type WithinReachRow,
} from '../api.ts';
import { unmarkedOnVisitWords } from '../checks.ts';
import { Card, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { CallLink } from '../components/CallLink.tsx';
import { BOX_SIZE } from '../components/Field.tsx';
import { DataTable, useWideView, type DataColumn } from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice } from '../components/Notice.tsx';
import { GapPill, NotToldPill, VisitStatusPill } from '../components/StatusPills.tsx';
import { NobodyAssigned, crewNames, needsACleaner, onTheCrew } from '../components/VisitBits.tsx';
import {
  addDays,
  clockTime,
  dayOf,
  daysBetween,
  formatLocalWindow,
  formatWindow,
  timeOfDayOf,
  isDay,
  money,
  shortDay,
  weekdayName,
} from '../format.ts';
import { CHANNEL_LABELS, PROMISE_CODE_LABELS } from '../labels.ts';
import { closeToFiringPanel, ruleChannelWords, withinReachWords } from '../reports.ts';

/**
 * How many rows a panel draws. It is a doorway and not a list: the screen behind the header is
 * where the rest of them are. It caps the ROWS and never the digit beside the heading, which is
 * always the whole count, so a panel showing five of seven says seven and the rail agrees.
 */
const PANEL_ROWS = 5;

/** "Today", "Tomorrow", "Yesterday", or the weekday, relative to the server's day. */
function relativeName(day: string, today: string): string {
  if (day === today) return 'Today';
  if (day === addDays(today, 1)) return 'Tomorrow';
  if (day === addDays(today, -1)) return 'Yesterday';
  return weekdayName(day);
}

/** A whole number of days, in words. One definition, so no pill in this file reads "1 days". */
function dayWords(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

/** How long something has been waiting, in whole days, measured against the server's own day. */
function waitingWords(instant: string, today: string): string {
  const days = daysBetween(dayOf(instant) ?? today, today);
  if (days === null || days < 1) return 'Today';
  return dayWords(days);
}

/**
 * How far past its due date an invoice is. The server sets `late` from the same comparison, so a
 * day it cannot count is still late; it just says so without a number.
 */
function lateWords(dueOn: string | null, today: string): string {
  const days = dueOn === null ? null : daysBetween(dueOn, today);
  if (days === null || days < 1) return 'Late';
  return dayWords(days);
}

/**
 * The answer to draw for the two days on screen, or null when the one in hand is not about them.
 *
 * The day is state that changes the instant somebody presses Tomorrow or types a date, and the
 * read that answers for the new day lands later. Between the two, the previous day's rows are
 * still the only rows there are, and filtering them by the new day matches nothing, which draws
 * "No visit is booked for this day" over a day that has visits. That notice is a claim about the
 * business, so it may only be drawn from an answer about the day it is drawn under.
 *
 * `GET /api/schedule` echoes the range it was asked for, so the comparison is exact and needs no
 * second piece of state to remember what is in flight.
 */
export function scheduleForDay(
  schedule: Schedule | null,
  from: string,
  to: string,
): Schedule | null {
  if (schedule === null) return null;
  return schedule.from === from && schedule.to === to ? schedule : null;
}

export function Today({
  me,
  heading = 'Today',
  subtitle = 'Who is at which house, and how to reach them.',
}: {
  me: Me;
  /**
   * THE WORDS AT THE TOP, PASSED IN BY THE ROUTE. This screen answers on two paths, `/today` and
   * `/schedule`, and until now both read "Today", so somebody who pressed Schedule in the rail and
   * got a page headed Today had reason to think the press had not worked. One component, two
   * headings: the door you came in by names the page, and the page itself is the same page.
   */
  heading?: string;
  subtitle?: string;
}) {
  const wide = useWideView();
  const [shown, setShown] = useState(me.today);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const next = addDays(shown, 1);
  const answer = scheduleForDay(schedule, shown, next);

  useEffect(() => {
    let live = true;
    setError(null);
    getSchedule(shown, next)
      .then((answer) => {
        if (live) setSchedule(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The schedule did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [shown, next, attempt]);

  const strip: readonly { day: string; label: string }[] = [
    { day: addDays(me.today, -1), label: 'Yesterday' },
    { day: me.today, label: 'Today' },
    { day: addDays(me.today, 1), label: 'Tomorrow' },
  ];

  return (
    <div>
      <ScreenTitle title={heading} subtitle={subtitle} />

      {/* The row wraps rather than sticking out (ops #56): at 768 the rail leaves 472px, and the
          three words and the date box need about 660, so the date box goes to a line of its own. */}
      <div className="mb-4 md:mb-5 md:flex md:flex-wrap md:items-center md:justify-between md:gap-x-4 md:gap-y-3">
        <div className="grid grid-cols-3 gap-2 md:flex">
          {strip.map((item) => (
            <button
              key={item.day}
              type="button"
              onClick={() => setShown(item.day)}
              className={[
                'min-h-12 rounded-lg border px-2 text-sm font-medium md:px-5 md:text-base desk:min-h-9 desk:px-4',
                item.day === shown
                  ? 'border-navy bg-navy text-cream'
                  : 'border-navy/25 bg-parchment text-navy',
              ].join(' ')}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="mt-4 md:mt-0 md:flex md:items-center md:gap-2.5">
          <label
            htmlFor="jump-day"
            className="block text-sm font-medium md:text-xs md:tracking-wide md:text-navy/55 md:uppercase"
          >
            Jump to a day
          </label>
          <input
            id="jump-day"
            type="date"
            value={shown}
            onChange={(event) => {
              if (isDay(event.target.value)) setShown(event.target.value);
            }}
            className={`mt-1 block w-full rounded-lg border border-navy/25 bg-parchment text-navy md:mt-0 md:w-45 ${BOX_SIZE}`}
          />
        </div>
      </div>

      {/* About the business day rather than the day on the strip, so it stays put while another
          day is read, and it sits above the days because it is about every one of them. */}
      {schedule ? <ScopeGapNotices gaps={schedule.scopeGaps ?? []} /> : null}

      {error ? (
        <ErrorNotice title="The schedule did not load" message={error.message} onRetry={reload} />
      ) : schedule === null ? (
        <Busy what="the schedule" />
      ) : (
        /* BESIDE THE DAYS ONLY FROM XL (ops #56). At md the 392px column left the days about
           fifty pixels; below xl the panels stand under the two days instead. At xl the column
           is 360px, so the day's table fits at 1366 beside it, and from 2xl the artboard's 392. */
        <div className="md:grid md:grid-cols-1 md:items-start md:gap-6 xl:grid-cols-[minmax(0,1fr)_360px] 2xl:grid-cols-[minmax(0,1fr)_392px]">
          {/* The four panels are not about the day on the strip, so they keep their place and
              their answers while a new day is being read. Only the two day sections wait. */}
          <div className="min-w-0">
            {answer === null ? (
              <Busy what="the schedule" />
            ) : (
              [shown, next].map((day, index) => {
                const rows = answer.visits.filter((row) => row.scheduledDate === day);
                return (
                  <section key={day} className="mb-6 md:mb-5">
                    <h2 className="mb-2 font-serif text-xl leading-7">
                      {relativeName(day, me.today)}
                      <span className="ml-2 text-base text-navy/60">{shortDay(day)}</span>
                    </h2>
                    {rows.length === 0 ? (
                      <EmptyNotice title="Nothing on the calendar">
                        <p>
                          No visit is booked for this day. A standing arrangement puts one here from
                          a household's page.
                        </p>
                      </EmptyNotice>
                    ) : (
                      <DataTable
                        label={`Visits on ${shortDay(day)}`}
                        groups={[{ key: day, rows }]}
                        columns={VISIT_COLUMNS}
                        rowKey={(row) => row.id}
                        rowClassName={(row) => (row.status === 'cancelled' ? 'opacity-70' : '')}
                        headerless={index > 0}
                        card={(row) => <VisitCard row={row} />}
                      />
                    )}
                    <MakeUpSlots
                      slots={(answer.makeUpSlots ?? []).filter((slot) => slot.day === day)}
                      rows={rows}
                    />
                  </section>
                );
              })
            )}
          </div>

          {wide ? <Attention today={schedule.today} /> : null}
        </div>
      )}

      <p className="mt-2 text-sm">
        <Link
          to="/closed-days"
          className="inline-flex min-h-11 desk:min-h-9 items-center text-navy/70 underline"
        >
          Days we are closed
        </Link>
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * A service line no new job sheet will open for, at both widths.
 * ------------------------------------------------------------------ */

/**
 * The sentence for one line. "No new" and never "no": a sheet somebody already froze still opens,
 * and saying otherwise would send the office to mend a door that is not shut. When some of the
 * line's homes are covered, by words of their own or by a version their agreement names, it says
 * how many are not rather than all of them.
 */
function scopeGapWords(gap: ScopeGap): string {
  const line = SERVICE_LINE_LABELS[gap.serviceLine];
  return gap.homes < gap.ofHomes
    ? `${line} has no scope in force at ${gap.homes} of its ${gap.ofHomes} homes with visits still to come, so no new job sheet will open there.`
    : `${line} has visits still to come and no scope in force, so no new job sheet for them will open.`;
}

/** One coral notice per line, each with the way to the screen where the words are published. */
function ScopeGapNotices({ gaps }: { gaps: readonly ScopeGap[] }) {
  if (gaps.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-3 md:mb-5">
      {gaps.map((gap) => (
        <div
          key={gap.serviceLine}
          className="rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-4"
        >
          <p className="text-sm">{scopeGapWords(gap)}</p>
          <Link
            to="/settings"
            className="mt-3 inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
          >
            Publish and approve its scope in Settings
          </Link>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The table, at md and above. The same rows the cards are drawn from.
 * ------------------------------------------------------------------ */

const VISIT_COLUMNS: readonly DataColumn<ScheduleRow>[] = [
  {
    key: 'window',
    header: 'Window',
    width: '168px',
    cellClassName: 'font-semibold',
    cell: (row) => formatWindow(row.windowStart, row.windowEnd) ?? 'No window set',
  },
  {
    key: 'household',
    header: 'Household',
    // THE COLUMN THAT TAKES THE REST (ops #56). At 166px it cut "The Whitfield household" to
    // "The Whitfield hous..." on every screen, a 1920 monitor with 700px of empty status column
    // included. With no width of its own it takes what the others leave, and the name wraps
    // rather than being cut where that is little.
    cell: (row) => (
      <Link
        to={`/visits/${row.id}`}
        className="flex min-h-11 items-center font-serif text-base break-words desk:min-h-9"
      >
        {row.clientName}
      </Link>
    ),
  },
  {
    key: 'town',
    header: 'Town',
    width: '112px',
    cellClassName: 'text-navy/75',
    cell: (row) => row.town,
  },
  {
    key: 'crew',
    header: 'Crew',
    width: '124px',
    // Who is going: a cleaner who declined in the app is not (ops #74 item 5), and a crew that all
    // declined is the gap the status column names.
    cell: (row) =>
      crewNames(row.cleaners) ?? (
        <span className="text-coral">
          {needsACleaner(row.cleaners) ? 'Declined' : 'Nobody assigned'}
        </span>
      ),
  },
  {
    key: 'status',
    header: 'Status',
    // A share, so the pills stand side by side where the table is wide and stack where it is
    // not. Pills, not a sentence, so it keeps less than a share's usual floor: at 1366 the day's
    // table then fits beside the panels without scrolling in its frame (ops #56).
    width: '16%',
    minWidth: '104px',
    cell: (row) => (
      <div className="flex flex-wrap gap-1.5">
        <VisitStatusPill status={row.status} />
        {row.pendingChange ? <NotToldPill /> : null}
        {row.callOff ? <GapPill>Cannot come</GapPill> : null}
        {needsACleaner(row.cleaners) ? <GapPill>Needs a cleaner</GapPill> : null}
        {/* An unmarked cleaner still on a clean to come (ops #85): the card says who in a sentence. */}
        {unmarkedOnVisitWords(row.unmarkedCleaners) ? (
          <span title={unmarkedOnVisitWords(row.unmarkedCleaners) ?? undefined} data-unmarked>
            <GapPill>No checks mark</GapPill>
          </span>
        ) : null}
        {/* The phone card says this in a sentence. Adding a contact is desk work, so the gap has
            to be legible from the desk as well, and it belongs where the other gaps already are. */}
        {row.contact ? null : <GapPill>No contact</GapPill>}
      </div>
    ),
  },
];

/**
 * One visit, on a phone. The top block opens the visit; the call buttons sit below it rather than
 * inside it, because a link inside a link is not HTML and a mis-tap that dials a client is worse
 * than one that opens a page.
 */
function VisitCard({ row }: { row: ScheduleRow }) {
  const window = formatWindow(row.windowStart, row.windowEnd);
  return (
    <Card className={row.status === 'cancelled' ? 'opacity-70' : ''}>
      <Link to={`/visits/${row.id}`} className="block min-h-11 desk:min-h-9">
        <p className="text-lg font-semibold">{window ?? 'No arrival window set'}</p>
        <h3 className="font-serif text-lg leading-tight">{row.clientName}</h3>
        <p className="text-sm text-navy/75">
          {row.addressLine1}, {row.town}
          {row.siteLabel ? ` (${row.siteLabel})` : ''}
        </p>
      </Link>

      <div className="mt-2 flex flex-wrap gap-2">
        <VisitStatusPill status={row.status} />
        <Pill>{VISIT_KIND_LABELS[row.kind]}</Pill>
        {row.pendingChange ? <NotToldPill /> : null}
        {row.callOff ? <GapPill>Cannot come</GapPill> : null}
        {needsACleaner(row.cleaners) ? <GapPill>Needs a cleaner</GapPill> : null}
      </div>
      {row.callOff ? (
        <p className="mt-2 text-sm text-coral" data-call-off>
          {callOffWords(row.callOff)}
        </p>
      ) : null}
      {needsACleaner(row.cleaners) ? (
        <p className="mt-2 text-sm text-coral" data-needs-a-cleaner>
          {needsACleanerWords(row.cleaners)}
        </p>
      ) : null}
      {unmarkedOnVisitWords(row.unmarkedCleaners) ? (
        <p className="mt-2 text-sm text-coral" data-unmarked>
          {unmarkedOnVisitWords(row.unmarkedCleaners)}
        </p>
      ) : null}

      <div className="mt-3">
        {row.cleaners.length === 0 ? (
          <NobodyAssigned />
        ) : onTheCrew(row.cleaners).length === 0 ? null : (
          <ul className="flex flex-wrap gap-2">
            {onTheCrew(row.cleaners).map((cleaner) => (
              <li key={cleaner.id} className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{cleaner.displayName}</span>
                {cleaner.phoneE164 ? (
                  <CallLink phoneE164={cleaner.phoneE164} />
                ) : (
                  <span className="text-xs text-navy/60">no number</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-3 border-t border-navy/10 pt-3">
        {row.contact ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-navy/75">{row.contact.displayName}</span>
            <CallLink phoneE164={row.contact.phoneE164} />
          </div>
        ) : (
          <p className="text-sm text-coral">
            No primary contact on file. Nobody here can be told when a visit moves.
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * "Maria declined. Put somebody on it, or them back, through Change this visit.": who declined, and
 * where the office acts (ops #74 item 5). The reason is the visit page's, which the card opens.
 */
export function needsACleanerWords(cleaners: readonly CrewOnVisit[]): string {
  const who = cleaners.map((cleaner) => cleaner.displayName);
  const names = who.length === 1 ? who[0] : `${who.slice(0, -1).join(', ')} and ${who.at(-1)}`;
  return `${names} declined. Put somebody on it, or them back, through Change this visit.`;
}

/** "Maria cannot come, illness, heard 6:10 pm": who, the word they gave, when the office heard. */
export function callOffWords(callOff: ScheduleCallOff): string {
  const heard = timeOfDayOf(callOff.heardAt);
  return `${callOff.cleanerName} cannot come, ${callOff.reason}${heard ? `, heard ${clockTime(heard)}` : ''}`;
}

/**
 * EACH CLEANER'S MAKE-UP SLOT ON THE DAY (ops #85, `docs/plan-call-off.md` section 11), open or
 * filled by the clean that took it. Drawn quiet: a slot kept open is the plan working.
 */
function MakeUpSlots({
  slots,
  rows,
}: {
  slots: readonly MakeUpSlot[];
  rows: readonly ScheduleRow[];
}) {
  if (slots.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {slots.map((slot) => {
        const filledBy = rows.find((row) => slot.visitIds.includes(row.id));
        return (
          <li
            key={`${slot.cleanerId}-${slot.day}`}
            className="rounded-lg border border-navy/12 bg-cream px-3 py-2 text-sm"
            data-make-up-slot
          >
            Make-up slot: {slot.displayName},{' '}
            {formatLocalWindow(slot.windowStartLocal, slot.windowEndLocal)},{' '}
            {slot.visitIds.length === 0
              ? 'open'
              : `filled by the clean at ${filledBy?.clientName ?? 'a household'}`}
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------------ *
 * The four panels, at md and above.
 * ------------------------------------------------------------------ */

/**
 * Each panel is one question and one endpoint, and each keeps its OWN refusal: the money read asks
 * for a recent sign-in, so a 403 there must not blank the three panels beside it that answered.
 */
function Attention({ today }: { today: string }) {
  /**
   * FIVE NAMES, AND THE ENDPOINT ANSWERS FIVE. This panel used to fetch every open enquiry, every
   * column of it, and throw away all but the first few, which was the whole enquiries screen's own
   * read made on a dashboard that already makes four others. `GET /api/enquiries/attention` is four
   * columns in one statement, ordered oldest first, and its predicate is the same one the rail's
   * digit counts, so the number beside the word and the names in this panel cannot disagree.
   *
   * THE DIGIT COMES FROM `total`, NEVER FROM `enquiries.length`. The four panels beside this one
   * count a whole list and slice only the rows they draw, so their headings say how many there are.
   * This one asks the server for five, and a heading reading the length of that page would top out
   * at five however many people were waiting: the rail would say seven and the panel five, on the
   * same predicate, on the same screen.
   */
  const enquiries = useOnce<AttentionPanel>(
    () => listAttentionEnquiries(PANEL_ROWS),
    'The enquiries did not load.',
  );
  const invoices = useOnce(() => listInvoices(true, null, '/today'), 'The invoices did not load.');
  const promises = useOnce<BreachList>(() => listBreaches('open'), 'The promises did not load.');
  /**
   * The fifth panel, and the only one that is usually not drawn at all. Three statements on the
   * server, which is what lets it join the four above without making this column expensive.
   */
  const rules = useOnce<{ rules: readonly WithinReachRow[] }>(
    () => getKillRulesWithinReach(),
    'The rules did not load.',
  );

  // Already the right rows in the right order: the server applied the predicate and the cap.
  const waiting = enquiries.data?.enquiries ?? [];
  // How many are waiting, which is not how many were sent.
  const waitingCount = enquiries.data?.total ?? 0;
  const late = (invoices.data?.invoices ?? []).filter((row) => row.late);
  const broken = promises.data?.breaches ?? [];
  const untold = promises.data?.untoldChanges ?? [];
  const close = rules.data?.rules ?? [];
  const closeDraw = closeToFiringPanel(rules.data !== null, close.length, rules.error !== null);

  return (
    <div className="flex flex-col gap-3">
      <Panel
        title="Nobody has replied yet"
        to="/enquiries"
        count={waitingCount}
        state={<Waiting on={enquiries} empty="Every enquiry has an answer." />}
      >
        {waiting.map((row) => (
          <PanelRow
            key={row.id}
            title={row.name}
            detail={CHANNEL_LABELS[row.sourceChannel]}
            mark={<GapPill>{waitingWords(row.createdAt, enquiries.data?.today ?? today)}</GapPill>}
          />
        ))}
      </Panel>

      <Panel
        title="Invoices that are late"
        to="/money"
        count={late.length}
        state={<Waiting on={invoices} empty="Nothing is past its due date." />}
      >
        {late.slice(0, PANEL_ROWS).map((row) => (
          <PanelRow
            key={row.id}
            serif
            title={row.clientName}
            detail={`Visit ${shortDay(row.visitDate)}, owed ${money(row.balanceCents)}`}
            mark={<GapPill>{lateWords(row.dueOn, invoices.data?.today ?? today)}</GapPill>}
          />
        ))}
      </Panel>

      <Panel
        title="Broken promises"
        to="/breaches"
        count={broken.length}
        state={<Waiting on={promises} empty="The nightly check found nothing." />}
      >
        {broken.slice(0, PANEL_ROWS).map((row) => (
          <PanelRow
            key={row.id}
            title={PROMISE_CODE_LABELS[row.code]}
            detail={[
              row.clientName ?? 'No household on this one',
              row.visitDate ? `visit ${shortDay(row.visitDate)}` : null,
            ]
              .filter(Boolean)
              .join(', ')}
            mark={<GapPill>Open</GapPill>}
          />
        ))}
      </Panel>

      <Panel
        title="Changes nobody has resolved"
        to="/breaches"
        count={untold.length}
        state={<Waiting on={promises} empty="Every change is told or explained." />}
      >
        {untold.slice(0, PANEL_ROWS).map((row) => (
          <PanelRow
            key={row.id}
            to={`/visits/${row.visitId}`}
            serif
            title={row.clientName}
            detail={[row.town, VISIT_CHANGE_KIND_LABELS[row.kind], shortDay(row.scheduledDate)]
              .filter(Boolean)
              .join(', ')}
            mark={<NotToldPill />}
          />
        ))}
      </Panel>

      {/*
       * LAST, AND USUALLY NOT THERE AT ALL. The four above are people waiting on this business
       * today; this one is money, and a marketing rule reaching its threshold is a decision to
       * make this week rather than a call to make this morning. Since ops #48 a rule that actually
       * fires is also told to both owners, once, as a `kill_rule_fired` notification raised by the
       * morning's checks (`api/src/reports/kill-rule-firings.ts`); a rule only close to firing is
       * this line and nothing more.
       *
       * So it is drawn only when a rule is actually near firing, which on almost every morning of
       * this business is never. A panel that says "nothing is close" every day is a panel somebody
       * stops reading, and this one has to be believed the day it appears.
       */}
      {closeDraw === 'nothing' ? null : (
        <Panel
          title="A rule is close to firing"
          to="/sources"
          count={closeDraw === 'rules' ? close.length : 0}
          state={<Waiting on={rules} empty="No rule is near its threshold." />}
        >
          {close.slice(0, PANEL_ROWS).map((row) => (
            <PanelRow
              key={`${row.channel}-${row.ruleText}`}
              title={ruleChannelWords(row.channel)}
              detail={row.ruleText}
              mark={<GapPill>{withinReachWords(row, today)}</GapPill>}
            />
          ))}
        </Panel>
      )}
    </div>
  );
}

/** One request, made once, when the panels appear. None of the three answers change with the day. */
function useOnce<T>(load: () => Promise<T>, fallback: string) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let live = true;
    load()
      .then((answer) => {
        if (live) setData(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(thrown instanceof ApiError ? thrown : new ApiError(0, 'error', fallback));
      });
    return () => {
      live = false;
    };
    // Deliberately once: the panels are opened, read, and acted on from their own screens.
  }, []);

  return { data, error };
}

/**
 * What a panel says instead of rows: still loading, refused, or answered and empty. A refusal is
 * the server's own sentence, kept whole, with the sign-in link it sent when it wants one.
 */
function Waiting<T>({
  on,
  empty,
}: {
  on: { data: T | null; error: ApiError | null };
  empty: string;
}) {
  if (on.error) {
    return (
      <div className="px-4 py-3">
        <p className="text-sm">{on.error.message}</p>
        {needsStepUp(on.error) && on.error.signInPath ? (
          <a
            href={on.error.signInPath}
            className="mt-2 inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
          >
            Sign in again to see this
          </a>
        ) : null}
      </div>
    );
  }
  if (on.data === null) {
    return (
      <p className="px-4 py-3 text-sm text-navy/60" role="status">
        Loading.
      </p>
    );
  }
  return <p className="px-4 py-3 text-sm text-navy/65">{empty}</p>;
}

/**
 * One panel. The header is the link to the screen that owns the list, and it is a 44px target
 * because it is the thing you press once you have decided the panel is where today's work is.
 */
function Panel({
  title,
  to,
  count,
  state,
  children,
}: {
  title: string;
  to: string;
  count: number;
  /** Drawn instead of the rows when there are none: loading, refused, or nothing to report. */
  state: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-navy/12 bg-parchment shadow-[0_1px_2px_rgba(22,41,63,0.06)]">
      <Link
        to={to}
        className="flex min-h-11 desk:min-h-9 items-center gap-2 border-b border-navy/12 px-4 hover:text-gold"
      >
        <h3 className="grow font-serif text-base">{title}</h3>
        {count > 0 ? <span className="text-xs text-navy/55">{count}</span> : null}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className="h-4 w-4 shrink-0"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 5l5 5-5 5" />
        </svg>
      </Link>
      {count === 0 ? state : <ul className="flex flex-col">{children}</ul>}
    </section>
  );
}

/** One line in a panel: what it is, one line about it, and the coral mark that says it is a gap. */
function PanelRow({
  title,
  detail,
  mark,
  serif,
  to,
}: {
  title: string;
  detail: string;
  mark: ReactNode;
  /** A household is set in the serif, the way it is everywhere else. A person's name is not. */
  serif?: boolean;
  to?: string;
}) {
  const body = (
    <>
      <span className="flex min-w-0 grow flex-col gap-px">
        <span
          className={
            serif
              ? 'truncate font-serif text-[15px] leading-[18px]'
              : 'truncate text-sm leading-[18px] font-medium'
          }
        >
          {title}
        </span>
        <span className="truncate text-xs leading-4 text-navy/65">{detail}</span>
      </span>
      {mark}
    </>
  );
  return (
    <li className="border-b border-navy/8 last:border-b-0">
      {to ? (
        <Link to={to} className="flex min-h-11 desk:min-h-9 items-center gap-3 px-4 py-1.5">
          {body}
        </Link>
      ) : (
        <span className="flex min-h-11 desk:min-h-9 items-center gap-3 px-4 py-1.5">{body}</span>
      )}
    </li>
  );
}
